// Persistent record of chunk-defined entities the player has "consumed" -
// killed, collected, or opened. Keyed by the per-entity hash from
// generateEntityHash(), which folds in the world name and spawn position, so
// the record survives world changes and chunk unload/reload. unpackChunk
// consults it to avoid respawning consumed entities.
//
// Backed by localStorage so it also survives a page reload. Call clear() (or
// wipe the storage key) to reset - handy when testing.
class InteractionState {
    private static STORAGE_KEY = 'piti_interactionState';
    private static consumed: Set<string> | null = null;

    private static ensureLoaded(){
        if(InteractionState.consumed){
            return;
        }
        InteractionState.consumed = new Set<string>();
        try {
            let raw = localStorage.getItem(InteractionState.STORAGE_KEY);
            if(raw){
                for(let hash of JSON.parse(raw)){
                    InteractionState.consumed.add(hash);
                }
            }
        } catch(e) {
            //localStorage unavailable/blocked - fall back to in-memory only
        }
    }

    private static persist(){
        try {
            localStorage.setItem(InteractionState.STORAGE_KEY, JSON.stringify([...InteractionState.consumed!]));
        } catch(e) {
            //ignore write failures, the in-memory set still works this session
        }
    }

    static markConsumed(hash: string){
        InteractionState.ensureLoaded();
        if(!InteractionState.consumed!.has(hash)){
            InteractionState.consumed!.add(hash);
            InteractionState.persist();
        }
    }

    static isConsumed(hash: string): boolean {
        InteractionState.ensureLoaded();
        return InteractionState.consumed!.has(hash);
    }

    static clear(){
        InteractionState.consumed = new Set<string>();
        InteractionState.persist();
    }

    static total(): number {
        InteractionState.ensureLoaded();
        return InteractionState.consumed!.size;
    }

    // Aggregated view for the stats screen: how many entities of each kind were
    // consumed. The kind is the prefix of the hash from generateEntityHash
    // ("chicken_ab12" -> "chicken"), sorted most-consumed first.
    static getStats(): {kind: string, count: number}[] {
        InteractionState.ensureLoaded();
        let counts: {[kind: string]: number} = {};
        for(let hash of InteractionState.consumed!){
            let kind = hash.substring(0, hash.lastIndexOf('_')) || hash;
            counts[kind] = (counts[kind] || 0) + 1;
        }
        return Object.keys(counts)
            .map(kind => ({kind, count: counts[kind]}))
            .sort((a, b) => b.count - a.count);
    }
}

export default InteractionState;
